import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import _ from "lodash";
import { SELECTED_SECTION_FILL_PAINT, USERS_SECTION_FILL_PAINT } from "@src/components/map-view/map.constants";
import { PolygonFeature } from "@src/components/map-view/map.types";
import {
	createFillLayer,
	groupFeaturesBySection,
	normalizeName,
	queryMapPolygonFeatures,
} from "@src/components/map-view/map.utils";
import { getComparableItemsFromDictionary, getRows, getSections, IIgnoredListing } from "@src/components/utils/autopricerUtils";
import { ActiveSeatGeekListing, MatchStatus } from "@src/store/marketplace/seatgeek/getActiveListingsSlice";
import { IPOSSettings } from "@src/store/pos/getPOSSettingsSlice";
import {
	EventInventoryRecord,
	invalidateEventInventoryCache,
	getInventoryCacheEventId,
} from "@src/store/pricing/queryEventInventoryAllSlice";
import { selectionSlice } from "@src/store/pricing/selectionSlice";
import { RootState } from "@src/store/store";
import {
	convertMapToRecord,
	difference,
	getUniqueArrayValues,
	groupByProperty,
	intersection,
	mapDictionaryKeysToValues,
	mapProperties,
	sortCompareFnFactory,
	toggleArrayElement,
	transformMapValues,
	union,
} from "@src/utils/array.utils";
import { FillLayer, MapboxMap } from "react-map-gl";

import { calculatePossibleSplits } from "@src/components/utils/lowestComparablesUtils";
import {
	ExcludeItemLabeEnum,
	TExcludeMethodItem,
} from "@src/pages/home/children/pricing/components/manage-inventory/autopricer/ui/excludeMethodItems";
import { TGroupName } from "@src/pages/home/children/pricing/components/manage-inventory/autopricer/ui/ExcludeMethods";
import { ApiProvider } from "@src/store/ApiProvider";
import { createAppAsyncThunk } from "@src/store/hooks";
import { pricingActions } from "..";
import { selectSeasonActiveListing } from "./getSeasonInventory";

export interface ToggleExclusionsPayload {
	type: TGroupName;
	payload: TExcludeMethodItem;
}

export interface ComparableItem {
	name: string;
	minPrice: number;
	listings: number;
	tickets: number;
}

export type FeaturesDictionary = Record<string, PolygonFeature>;
export type ListingsDictionary = Record<string, Array<ActiveSeatGeekListing>>;
export type SectionsByRow = Record<string, Array<string>>;
export type SaveRuleStatus = "idle" | "pending" | "saved" | "error";

export interface IAutoPricerState {
	listingsBySection: ListingsDictionary;
	featuresBySection: FeaturesDictionary;
	sectionsByRow: SectionsByRow;
	comparableSections: Array<ComparableItem>;
	ignoredOldSeatGeekIds: Array<string>;
	selectedSectionNames: Array<string>;
	selectedRowNames: Array<string>;
	usersSectionNames: Array<string>;
	compareAgainstConsignors: boolean;
	floorPricePercentage: number;
	ceilingPricePercentage: number;
	singleSplitsMapping: Array<number>;
	matchAll: boolean;
	saveRuleStatus: SaveRuleStatus;
	traitExclusions: Array<TExcludeMethodItem>;
	deliveryExclusions: Array<TExcludeMethodItem>;
	allowPriceIncrease: boolean;
	isPercentage: boolean;
	hasUnsavedChanges: boolean;
	persistedSelection: { eventId: number; sections: Array<string>; rows: Array<string> } | null;
}

const initialState: IAutoPricerState = {
	listingsBySection: {},
	isPercentage: false,
	featuresBySection: {},
	sectionsByRow: {},
	comparableSections: [],
	ignoredOldSeatGeekIds: [],
	selectedSectionNames: [],
	selectedRowNames: [],
	usersSectionNames: [],
	compareAgainstConsignors: false,
	floorPricePercentage: 90,
	ceilingPricePercentage: 300,
	singleSplitsMapping: [],
	matchAll: false,
	saveRuleStatus: "idle",
	traitExclusions: [],
	deliveryExclusions: [],
	allowPriceIncrease: true,
	hasUnsavedChanges: false,
	persistedSelection: null,
};

export interface InitializeAutopricerStatePayload {
	map: MapboxMap;
	activeSeatGeekListings: Array<ActiveSeatGeekListing>;
	eventInventoryData: Array<EventInventoryRecord>;
	POSSettings: IPOSSettings | null;
	quantity: number;
}

export interface LoadNewRulePayload {
	CeilingPrice?: number;
	FloorPrice?: number;
	TicketSplit?: Array<number>;
	Rows?: Array<string>;
	Sections?: Array<string>;
	CompareToEverything?: boolean;
	CompareAgainstOwnInventory?: boolean;
	Ignored_Listings?: Array<IIgnoredListing>;
	Delivery_Exclusions?: Array<TExcludeMethodItem>;
	TraitExclusions?: Array<TExcludeMethodItem>;
	NoComparablesNotification?: boolean;
	AllowPriceIncrease?: boolean;
	AmountUnder?: number;
	IsPercentage?: boolean;
}

export interface AutoPricerItemHistory {
	ItemId: number;
	ListingId: number;
	Section: string;
	Row: string;
	Seat: string;
	UserName: string;
	Activity: string;
	ActivityType: string;
	CreatedDate: string;
}

export const getItemPriceHistory = createAsyncThunk<Array<AutoPricerItemHistory>, string>(
	"autopricer/getItemHistory",
	async (payload, { rejectWithValue }) => {
		try {
			const data = await ApiProvider.default.get<Array<AutoPricerItemHistory>>(`/api/System/ViewItemHistory?${payload}`);
			return data;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

export const INACTIVE_LISTING_ID = -1;

const autoPricerSlice = createSlice({
	name: "autopricer",
	initialState,
	reducers: {
		initializeAutopricerState: (state, action: PayloadAction<InitializeAutopricerStatePayload>) => {
			const { map, activeSeatGeekListings, eventInventoryData, POSSettings, quantity } = action.payload;

			const normalizedActiveSeatGeekListings = mapProperties(activeSeatGeekListings, ["section", "row"], normalizeName);

			const listingsBySection = groupByProperty(normalizedActiveSeatGeekListings, "section");
			const listingsByRow = groupByProperty(normalizedActiveSeatGeekListings, "row");

			const sectionsByRow = transformMapValues(listingsByRow, (listings) => listings.map(({ section }) => section));

			const allMapboxSectionFeatures = queryMapPolygonFeatures(undefined, map, "section");
			const allMapboxRowFeatures = queryMapPolygonFeatures(undefined, map, "row");

			allMapboxSectionFeatures.forEach((mapboxSectionFeature) => {
				const normalizedSectionName = normalizeName(mapboxSectionFeature.properties.name);

				if (listingsBySection.has(normalizedSectionName)) return;

				listingsBySection.set(normalizedSectionName, []);
			});

			allMapboxRowFeatures.forEach((mapboxRowFeature) => {
				const originalSectionName = mapboxRowFeature.properties.parent_name;

				if (!originalSectionName) return;

				const normalizedSectionName = normalizeName(originalSectionName);
				const normalizedRowName = normalizeName(mapboxRowFeature.properties.name);

				const sectionListings = listingsBySection.get(normalizedSectionName);

				const inactiveListing: ActiveSeatGeekListing = {
					oldSeatGeekId: "",
					seatGeekListingId: INACTIVE_LISTING_ID,
					section: normalizedSectionName,
					row: normalizedRowName,
					quantity: 0,
					price: 0,
					match: 0,
					splits: [],
					instantDelivery: false,
				};

				if (!sectionListings) {
					listingsBySection.set(normalizedSectionName, [inactiveListing]);
				} else {
					const sectionRows = sectionListings.map(({ row }) => row);

					if (sectionRows.includes(normalizedRowName)) return;

					listingsBySection.set(normalizedSectionName, [...sectionListings, inactiveListing]);
				}

				const rowSections = sectionsByRow.get(normalizedRowName);

				sectionsByRow.set(normalizedSectionName, union(rowSections ?? [], normalizedSectionName));
			});

			const listingBySectionRecord = convertMapToRecord(listingsBySection);

			state.listingsBySection = listingBySectionRecord;

			state.featuresBySection = convertMapToRecord(groupFeaturesBySection(allMapboxSectionFeatures));

			state.sectionsByRow = convertMapToRecord(sectionsByRow);

			state.comparableSections = getComparableItemsFromDictionary(listingBySectionRecord);

			state.usersSectionNames = getUniqueArrayValues(eventInventoryData.map(({ Section }) => normalizeName(Section)));

			state.saveRuleStatus = "idle";

			state.compareAgainstConsignors = !!POSSettings?.Compare_Against_Own_Inventory;
			state.floorPricePercentage = POSSettings?.Floor_Price_Percentage ?? 90;
			state.ceilingPricePercentage = POSSettings?.Ceiling_Price_Percentage ?? 300;
			state.singleSplitsMapping = calculatePossibleSplits(POSSettings?.Custom_Split_Mapping, quantity);
		},
		selectSectionOnMap: (state, action: PayloadAction<string>) => {
			const sectionName = action.payload;

			const selectedSectionNames = toggleArrayElement(state.selectedSectionNames, sectionName);

			const allowedRowNames = getRows(state.listingsBySection, selectedSectionNames);

			state.selectedSectionNames = selectedSectionNames;

			state.selectedRowNames = intersection(allowedRowNames, state.selectedRowNames);
			state.hasUnsavedChanges = true;
		},
		addSection: (state, action: PayloadAction<string>) => {
			const { selectedSectionNames } = state;
			const sectionName = action.payload;

			if (selectedSectionNames.includes(sectionName)) return;

			state.selectedSectionNames = [...selectedSectionNames, sectionName];
			state.hasUnsavedChanges = true;
		},
		saveSelectedSections: (state, action: PayloadAction<Array<string>>) => {
			const selectedSectionNames = action.payload;

			if (_.xor(state.selectedSectionNames, selectedSectionNames).length) state.hasUnsavedChanges = true;

			const allowedRowNames = getRows(state.listingsBySection, selectedSectionNames);

			state.selectedSectionNames = selectedSectionNames;

			state.selectedRowNames = intersection(allowedRowNames, state.selectedRowNames);
		},
		saveSelectedRows: (state, action: PayloadAction<Array<string>>) => {
			const selectedRowNames = action.payload;

			if (_.xor(state.selectedRowNames, selectedRowNames).length) state.hasUnsavedChanges = true;

			state.selectedRowNames = selectedRowNames;

			if (state.selectedSectionNames.length > 0) return;

			const mappedRowSections = mapDictionaryKeysToValues(state.sectionsByRow, selectedRowNames).flat();

			state.selectedSectionNames = getUniqueArrayValues(mappedRowSections);
		},
		toggleMatchAll: (state) => {
			const newMatchAllValue = !state.matchAll;

			const selectedSectionNames = newMatchAllValue ? getSections(state.listingsBySection) : [];
			const selectedRowNames = newMatchAllValue ? getRows(state.listingsBySection) : [];

			state.selectedSectionNames = selectedSectionNames;
			state.selectedRowNames = selectedRowNames;
			state.matchAll = newMatchAllValue;
			state.hasUnsavedChanges = true;
		},
		ignoreListing: (state, action: PayloadAction<string>) => {
			const { ignoredOldSeatGeekIds } = state;
			const oldSeatGeekId = action.payload;

			state.ignoredOldSeatGeekIds = toggleArrayElement(ignoredOldSeatGeekIds, oldSeatGeekId);
			state.hasUnsavedChanges = true;
		},
		toggleCompareAgainstConsignors: (state) => {
			state.compareAgainstConsignors = !state.compareAgainstConsignors;
			state.hasUnsavedChanges = true;
		},
		resetMapState: (state) => {
			state.selectedSectionNames = [];
			state.selectedRowNames = [];
			state.hasUnsavedChanges = true;
		},
		resetAutopricerState: (state, action: PayloadAction<{ keepSelectionForEventId?: number | null } | undefined>) => {
			const eventId = action.payload?.keepSelectionForEventId;
			const persistedSelection =
				eventId !== undefined && eventId !== null
					? { eventId, sections: [...state.selectedSectionNames], rows: [...state.selectedRowNames] }
					: null;
			return { ...initialState, persistedSelection };
		},
		restoreSelectionForEvent: (state, action: PayloadAction<number | null | undefined>) => {
			const persisted = state.persistedSelection;
			if (!persisted) return;

			if (persisted.eventId !== action.payload) {
				state.persistedSelection = null;
				return;
			}

			if (state.selectedSectionNames.length || state.selectedRowNames.length) return;
			state.selectedSectionNames = [...persisted.sections];
			state.selectedRowNames = [...persisted.rows];
		},
		toggleSingleSplitsMapping: (state, action: PayloadAction<number>) => {
			const currentSplits = state.singleSplitsMapping;
			const isIncluded = currentSplits.includes(action.payload);
			state.hasUnsavedChanges = true;

			if (isIncluded) {
				state.singleSplitsMapping = currentSplits.filter((x) => x !== action.payload);
				return;
			}
			state.singleSplitsMapping.push(action.payload);
		},
		bulkEditAutoPricer: (state, action: PayloadAction<LoadNewRulePayload>) => {
			const {
				Rows,
				Sections,
				CompareToEverything,
				CompareAgainstOwnInventory,
				Ignored_Listings,
				TicketSplit,
				Delivery_Exclusions,
				AllowPriceIncrease,
				TraitExclusions,
				IsPercentage,
			} = action.payload;

			const ignoredListings = Ignored_Listings?.map((x) => x.SeatGeekId) ?? state.ignoredOldSeatGeekIds;

			state.isPercentage = IsPercentage ?? state.isPercentage;
			state.matchAll = CompareToEverything ?? state.matchAll;
			state.selectedSectionNames = Sections ?? state.selectedSectionNames;
			state.selectedRowNames = Rows ?? state.selectedRowNames;
			state.compareAgainstConsignors = CompareAgainstOwnInventory ?? state.compareAgainstConsignors;
			state.ignoredOldSeatGeekIds = ignoredListings;
			state.singleSplitsMapping = TicketSplit ?? state.singleSplitsMapping;
			state.deliveryExclusions = Delivery_Exclusions ?? state.deliveryExclusions;
			state.traitExclusions = TraitExclusions ?? state.traitExclusions;
			state.allowPriceIncrease = AllowPriceIncrease ?? state.allowPriceIncrease;
		},
		toggleExclusions: (state, action: PayloadAction<ToggleExclusionsPayload>) => {
			const { type, payload } = action.payload;
			const key = type === "Exclude Traits" ? "traitExclusions" : "deliveryExclusions";

			const currentExclusions = state[key];
			const exists = currentExclusions.some((x) => x.id === payload.id);
			state.hasUnsavedChanges = true;
			if (exists) {
				state[key] = currentExclusions.filter((x) => x.id !== payload.id);
				return;
			}
			state[key].push(payload);
		},
		setExclusions: (state, action: PayloadAction<{ type: TGroupName; payload: Array<TExcludeMethodItem> }>) => {
			const { type, payload } = action.payload;
			const key = type === "Exclude Traits" ? "traitExclusions" : "deliveryExclusions";
			state[key] = payload;
			state.hasUnsavedChanges = true;
		},
		setPriceIncrease: (state, action: PayloadAction<boolean>) => {
			state.allowPriceIncrease = action.payload;
			state.hasUnsavedChanges = true;
		},
		setPercentage: (state, action: PayloadAction<boolean>) => {
			state.isPercentage = action.payload;
			state.hasUnsavedChanges = true;
		},
		markUnsavedChanges: (state) => {
			state.hasUnsavedChanges = true;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(saveRules.pending, (state) => {
				state.saveRuleStatus = "pending";
			})
			.addCase(saveRules.fulfilled, (state) => {
				state.saveRuleStatus = "saved";
			})
			.addCase(saveRules.rejected, (state) => {
				state.saveRuleStatus = "error";
			})
			.addCase(selectionSlice.actions.setEvent, (state, action) => {
				if (state.persistedSelection && state.persistedSelection.eventId !== action.payload.event?.Event_ID) {
					state.persistedSelection = null;
				}
			});
	},
});

const _selectSelectedSectionNames = (state: RootState) => state.autopricer.selectedSectionNames;
const _selectSelectedRowNames = (state: RootState) => state.autopricer.selectedRowNames;
const _selectUsersSectionNames = (state: RootState) => state.autopricer.usersSectionNames;
const _selectFeaturesBySection = (state: RootState) => state.autopricer.featuresBySection;
const _selectListingsBySection = (state: RootState) => state.autopricer.listingsBySection;
const _selectSingleSplitsMapping = (state: RootState) => state.autopricer.singleSplitsMapping;

export const selectComparableSections = (state: RootState) => state.autopricer.comparableSections;
export const selectSelectedSections = (state: RootState) => state.autopricer.selectedSectionNames;
export const selectSelectedRows = (state: RootState) => state.autopricer.selectedRowNames;
export const selectCompareAgainstConsignors = (state: RootState) => state.autopricer.compareAgainstConsignors;
export const selectIgnoredOldSeatGeekIds = (state: RootState) => state.autopricer.ignoredOldSeatGeekIds;
export const selectFloorPricePercentage = (state: RootState) => state.autopricer.floorPricePercentage;
export const selectCeilingPricePercentage = (state: RootState) => state.autopricer.ceilingPricePercentage;
export const selectSingleSplitsMapping = (state: RootState) => state.autopricer.singleSplitsMapping;
export const selectMatchAll = (state: RootState) => state.autopricer.matchAll;
export const selectSaveRuleStatus = (state: RootState) => state.autopricer.saveRuleStatus;
export const selectDeliveryExclusions = (state: RootState) => state.autopricer.deliveryExclusions;
export const selectTraitExclusions = (state: RootState) => state.autopricer.traitExclusions;
export const selectAllowPriceIncrease = (state: RootState) => state.autopricer.allowPriceIncrease;
export const selectIsPercentage = (state: RootState) => state.autopricer.isPercentage;
export const selectAutopricerHasUnsavedChanges = (state: RootState) =>
	state.autopricer.hasUnsavedChanges || state.getSeason.data.selectedIds.length > 0;

export const selectComparableRows = createSelector(
	[_selectSelectedSectionNames, _selectListingsBySection],
	(selectedSectionNames, listingsBySection) => {
		const selectedSectionListings =
			selectedSectionNames.length > 0
				? mapDictionaryKeysToValues(listingsBySection, selectedSectionNames).flat()
				: Array.from(Object.values(listingsBySection)).flat();

		const listingsByRow = convertMapToRecord(groupByProperty(selectedSectionListings, "row"));

		return getComparableItemsFromDictionary(listingsByRow);
	},
);

export const selectAutopricerMapFillLayers = createSelector(
	[_selectSelectedSectionNames, _selectUsersSectionNames, _selectFeaturesBySection, selectMatchAll],
	(selectedSectionNames, usersSectionNames, featuresBySection, matchAll) => {
		const fillLayers: Array<FillLayer> = [];

		if (matchAll) return fillLayers;

		const selectedSectionsFeatures = mapDictionaryKeysToValues(featuresBySection, selectedSectionNames);
		const usersSectionsFeatures = mapDictionaryKeysToValues(
			featuresBySection,
			difference(usersSectionNames, selectedSectionNames),
		);

		if (selectedSectionsFeatures.length > 0) {
			fillLayers.push(createFillLayer("selected-sections-fill-layer", SELECTED_SECTION_FILL_PAINT, selectedSectionsFeatures));
		}

		if (usersSectionsFeatures.length > 0) {
			fillLayers.push(createFillLayer("users-sections-fill-layer", USERS_SECTION_FILL_PAINT, usersSectionsFeatures));
		}

		return fillLayers;
	},
);

export const selectLowestComparableListings = createSelector(
	[
		_selectSelectedSectionNames,
		_selectSelectedRowNames,
		_selectListingsBySection,
		selectCompareAgainstConsignors,
		_selectSingleSplitsMapping,
		selectDeliveryExclusions,
	],
	(
		selectedSectionNames,
		selectedRowNames,
		listingsBySection,
		compareAgainstConsignors,
		singleSplitsMapping,
		deliveryExclusions,
	) => {
		const excludeInstantDelivery = deliveryExclusions.some((item) => item.label === ExcludeItemLabeEnum.instantDelivery);
		const excludedStockTypes = deliveryExclusions.filter((item) => item.stockType).map((item) => item.stockType);

		return mapDictionaryKeysToValues(listingsBySection, selectedSectionNames)
			.flat()
			.filter(
				(listing) =>
					listing.seatGeekListingId !== INACTIVE_LISTING_ID &&
					selectedRowNames.includes(listing.row) &&
					listing.match !== MatchStatus.USERS_LISTING &&
					(compareAgainstConsignors || listing.match !== MatchStatus.OTHER_CONSIGNOR_LISTING) &&
					(!singleSplitsMapping.length || listing.splits.some((split) => singleSplitsMapping.includes(split))) &&
					(!excludeInstantDelivery || !listing.instantDelivery) &&
					!excludedStockTypes.includes(listing.stockType),
			)
			.sort(sortCompareFnFactory<ActiveSeatGeekListing>("price"));
	},
);

export const selectSeasonLowestComparableListings = createSelector(
	[
		_selectSelectedSectionNames,
		_selectSelectedRowNames,
		selectCompareAgainstConsignors,
		_selectSingleSplitsMapping,
		selectDeliveryExclusions,
		selectSeasonActiveListing,
	],
	(
		selectedSectionNames,
		selectedRowNames,
		compareAgainstConsignors,
		singleSplitsMapping,
		deliveryExclusions,
		seasonActiveListing,
	) => {
		const excludeInstantDelivery = deliveryExclusions.some((item) => item.label === ExcludeItemLabeEnum.instantDelivery);
		const excludedStockTypes = deliveryExclusions.filter((item) => item.stockType).map((item) => item.stockType);

		return seasonActiveListing
			.filter(
				(listing) =>
					listing.seatGeekListingId !== INACTIVE_LISTING_ID &&
					selectedRowNames.includes(listing.row) &&
					selectedSectionNames.includes(listing.section) &&
					listing.match !== MatchStatus.USERS_LISTING &&
					(compareAgainstConsignors || listing.match !== MatchStatus.OTHER_CONSIGNOR_LISTING) &&
					(!singleSplitsMapping.length || listing.splits.some((split) => singleSplitsMapping.includes(split))) &&
					(!excludeInstantDelivery || !listing.instantDelivery) &&
					!excludedStockTypes.includes(listing.stockType),
			)
			.sort(sortCompareFnFactory<ActiveSeatGeekListing>("price"));
	},
);

export const selectLowestComparablePrice = createSelector(
	[selectLowestComparableListings, selectIgnoredOldSeatGeekIds],
	(lowestComparableListings, ignoredOldSeatGeekIds) => {
		return lowestComparableListings.find((listing) => !ignoredOldSeatGeekIds.includes(listing.oldSeatGeekId))?.price ?? 0;
	},
);

export const selectSeasonLowestComparablePrice = createSelector(
	[selectSeasonLowestComparableListings, selectIgnoredOldSeatGeekIds],
	(seasonLowestComparableListings, ignoredOldSeatGeekIds) => {
		return seasonLowestComparableListings.find((listing) => !ignoredOldSeatGeekIds.includes(listing.oldSeatGeekId))?.price ?? 0;
	},
);

export interface SetListingRulesPayload {
	ignores: [];
	listingIdFloors: Array<{
		ListingId: number;
		FloorPrice: number;
		CeilingPrice: number;
	}>;
	rules: {
		ListingId: number;
		SeatGeekListingId: number;
		MasterRuleId: number | null;
		venueId: number;
		AllRows: boolean;
		AllowPriceIncrease: boolean;
		CompareAgainstOwnInventory: boolean;
		CompareToEverything: boolean;
		FloorPrice: number;
		FloorNotification: boolean;
		CeilingPrice: number;
		CeilingNotification: boolean;
		NoComparablesNotification: boolean;
		AmountUnder: number;
		PctUnder: number;
		UseDefaultSplits: true;
		TicketSplit: string;
		Sections: Array<string>;
		Rows: Array<string>;
		TraitExclusions: string;
		Exclude_Outliers: number;
		Delivery_Exclusions: string;
		IgnoredListings: Array<{
			SeatGeekId: string;
			SeatGeekListingId: number;
		}>;
		Ignored_Listings: Array<{
			SeatGeekId: string;
			SeatGeekListingId: number;
		}>;
	};
}

export const setListingRules = createAppAsyncThunk(
	"autopricer/setListingRules",
	async (payload: SetListingRulesPayload, { dispatch, getState }) => {
		try {
			const response = await ApiProvider.default.post<boolean>("api/AutoPricer/SetListingRules", payload);

			const state = getState();
			const eventId = getInventoryCacheEventId(state);

			const newData = state.pricing.eventInventoryAll.data.map((x) =>
				x.Listing_ID === payload.rules.ListingId ? { ...x, Is_AutoPriced: true } : x,
			);

			dispatch(pricingActions.updateInventoryAll({ data: newData, eventId }));

			const globalInventoryData = state.pricing.globalInventory.data;
			if (globalInventoryData.length) {
				dispatch(
					pricingActions.updateGlobalInventory({
						data: globalInventoryData.map((x) =>
							x.Listing_ID === payload.rules.ListingId ? { ...x, Is_AutoPriced: true } : x,
						),
					}),
				);
			}

			if (eventId) {
				invalidateEventInventoryCache([eventId]);
				dispatch(
					pricingActions.queryEventInventoryAll({
						EventId: eventId,
						CompanyName: state.settings.companyName,
						skipCache: true,
					}),
				);
			}

			return response;
		} catch (error) {
			console.log(error);
		}
	},
);

export type SaveRulesPayload = SetListingRulesPayload["rules"] & {
	RuleName: string;
};

export const saveRules = createAppAsyncThunk(
	"autopricer/saveRules",
	async (payload: SaveRulesPayload) => await ApiProvider.default.post<boolean>("api/AutoPricer/SaveRules", payload),
);

export default autoPricerSlice;
